/* eslint-disable prefer-promise-reject-errors */
import formatQuery from 'utils/formatQuery'
import {push as pushState} from 'react-router-redux'
import {deleteToken} from 'redux/modules/currentUser/actions'
import isObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'
export const CALL_API = 'CALL_API'
const BASE_URL = __API_URL__
const NO_CONTENT = 204

function callApi(endpoint, config = {}) {
  const defaults = {
    headers: {
      'Accept': `application/vnd.divvit.v2+json`,
      'Content-Type': 'application/json;charset=UTF-8'
    }
  }

  function processRequestBody(contentType, body) {
    switch (contentType) {
      case 'multipart/form-data':
        const formData = new FormData()
        Object.keys(body).forEach(key => {
          formData.append(key, body[key])
        })
        return formData
      default:
        return JSON.stringify(config.body)
    }
  }

  function processRequestHeaders(headers) {
    const contentType = headers['Content-Type']
    switch (contentType) {
      case 'multipart/form-data':
        delete headers['Content-Type']
        return headers
      default:
        return headers
    }
  }

  if (config.body) {
    const headers = {...defaults.headers, ...config.headers}
    const contentType = headers['Content-Type']
    config.body = processRequestBody(contentType, config.body)
  }

  if (config.headers) {
    const headers = {...defaults.headers, ...config.headers}
    config.headers = processRequestHeaders(headers)
  }

  const queryParams = formatQuery(config.queryParams)

  function parseHeaders(response) {
    const headers = {}
    for (const header of response.headers.entries()) {
      headers[header[0]] = header[1]
    }
    return {headers, response}
  }

  function parseBody({headers, response}) {
    if (response.status === NO_CONTENT) {
      return {json: {}, response, headers}
    } else if (/application\/json/.test(headers['content-type'])) {
      return response.json().then(json => ({json, response, headers}))
    } else {
      return response.text().then(message => ({json: {message}, response, headers}))
    }
  }

  function checkStatus({json, response, headers}) {
    if (!response.ok) {
      return Promise.reject({...json, status: response.status})
    }
    return {json, headers}
  }

  function checkError(error) {
    if (error && (error.status === 403 || error.status === 401)) {
      return Promise.reject({
        message: 'Your session has expired, try to login again.',
        status: error.status
      })
    }
    if (error && error.status === 404) {
      return Promise.reject({
        message: 'Something went wrong, please try a little bit later.',
        status: error.status
      })
    }
    if (error && error.message === 'Failed to fetch') {
      return Promise.reject({
        message: 'Something went wrong, please try a little bit later.',
        status: 500
      })
    }

    const newError = error.error || error || new Error('Unknown error')
    const message = isString(newError) ? newError : (isObject(newError.message) ? 'Unknown error' : newError.message)
    return Promise.reject({
      message,
      status: error.status
    })
  }

  return fetch(BASE_URL + endpoint + (queryParams ? '?' + queryParams : ''), {...defaults, ...config})
    .then(parseHeaders)
    .then(parseBody)
    .then(checkStatus)
    .catch(checkError)
}

const fetching = {}
export default store => next => action => {
  const callAPI = action[CALL_API]

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action)
  }

  const {
    endpoint,
    types,
    method = 'GET',
    headers = {},
    queryParams = {},
    params,
    errorsHandled,
    body
  } = callAPI
  const {currentUser = {}} = store.getState()
  const {token} = currentUser

  if (!endpoint) {
    throw new Error('`endpoint` must be defined')
  }

  if (!Array.isArray(types) || types.length !== 3 || !types.every(type => typeof type === 'string')) {
    throw new Error('Expected an array of three string types.')
  }

  const [requestType, successType, errorType] = types
  if (token) {
    queryParams.access_token = token
  }

  const request = {endpoint, method, headers, body, queryParams}
  const requestHash = JSON.stringify(request)
  // We will return same Promise if we try to fetch same endpoint with same params while it's already been fetched
  if (fetching[requestHash]) { return fetching[requestHash] }

  next({type: requestType, request, params})
  const call = new Call((success, fail) => {
    callApi(endpoint, {method, body, headers, queryParams})
      .then(
        ({json, headers}) => {
          next({
            request,
            response: json,
            headers,
            type: successType,
            params
          })
          delete fetching[requestHash]
          success(json)
        },
        error => {
          if (error.status === 403 || error.status === 401) {
            next(deleteToken())
            next(pushState('/login'))
          }
          next({
            request,
            message: error.message ? error.message : error,
            errorsHandled,
            type: errorType,
            error: true,
            status: error.status,
            params
          })
          delete fetching[requestHash]
          fail(error)
        }
      )
  })

  fetching[requestHash] = call
  return call
}

export class Call {
  static resolve(arg) {
    return new Call((success, fail) => { success(arg) })
  }

  static reject(arg) {
    return new Call((success, fail) => { fail(arg) })
  }

  static all(calls) {
    return new Call((success, fail) => {
      let numberOfCalls = calls.length
      const results = []
      let error = false

      calls.forEach((call, index) => {
        call
          .success(result => {
            results[index] = result
            if (--numberOfCalls === 0 && !error) success(results)
          })
          .fail(err => {
            if (error) return
            fail(err)
            error = true
          })
      })
    })
  }

  constructor(fn) {
    this._successes = []
    this._fails = []
    this._state = 'pending'
    fn && fn(
      arg => this._onSuccess(arg),
      arg => this._onFail(arg)
    )
  }

  success(fn) {
    this._successes.push(fn)
    if (this._state === 'success') { this._onSuccess(this._resolveValue) }
    return this
  }

  fail(fn) {
    this._fails.push(fn)
    if (this._state === 'fail') { this._onFail(this._resolveValue) }
    return this
  }

  _onSuccess(arg) {
    this._state = 'success'
    this._resolveValue = arg
    this._successes.forEach(cb => {
      this._resolveValue = cb(this._resolveValue || arg) || this._resolveValue
    })
  }

  _onFail(arg) {
    this._state = 'fail'
    this._resolveValue = arg
    let index = 0
    const fails = this._fails
    const cbCount = fails.length

    while (index < cbCount) {
      const result = fails[index](this._resolveValue || arg)
      this._resolveValue = result || this._resolveValue
      index++
      if (result === false) break
    }
  }
}



// WEBPACK FOOTER //
// ./client/redux/middleware/api.js